import type * as Radix from '@radix-ui/react-accordion'

import {
  Accordion as BaseAccordion,
  AccordionContent as BaseAccordionContent,
  AccordionItem as BaseAccordionItem,
  AccordionTrigger as BaseAccordionTrigger,
} from './base'

/**
 * Accordion Configuration Options
 *
 * @property type - Controls accordion behavior
 *   - AccordionType.Single: Only one item can be open at a time.
 *   - AccordionType.Multiple: Multiple items can be open simultaneously
 *
 * @property collapsible - Controls collapse behavior
 *   - true: Active items can be closed by clicking them again
 *   - false: At least one item must remain open
 *
 * @property value - A string value for the accordion item.
 *   - All items within an accordion should use a unique value.
 */

export enum AccordionType {
  Single = 'single',
  Multiple = 'multiple',
}

export interface AccordionProps {
  items: AccordionItemProps[]
  type?: AccordionType
  collapsible?: boolean
  className?: string
  defaultValue?: string | string[]
  value?: string | string[]
  onValueChange?: (value: string | string[]) => void
}

interface AccordionItemProps {
  label: React.ReactNode
  content: React.ReactNode
  value: string
  className?: string
  contentClassName?: string
}

/**
 * @deprecated Use `Accordion` from `@moment/design-system-ui/accordion` instead.
 */
// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function Accordion({
  items,
  type = AccordionType.Single,
  collapsible = true,
  className,
  defaultValue,
  value,
  onValueChange,
}: AccordionProps) {
  const rootProps = {
    type,
    collapsible,
    className,
    defaultValue,
    value,
    onValueChange,
  } as Radix.AccordionSingleProps | Radix.AccordionMultipleProps

  return (
    <BaseAccordion {...rootProps}>
      {items.map((item, index) => {
        return (
          <BaseAccordionItem
            key={`accordion-item-${index}`}
            value={item.value}
            className={item.className}
          >
            <BaseAccordionTrigger>{item.label}</BaseAccordionTrigger>
            <BaseAccordionContent className={item.contentClassName}>
              {item.content}
            </BaseAccordionContent>
          </BaseAccordionItem>
        )
      })}
    </BaseAccordion>
  )
}
